/**
 * i18n maison pour catho-economat (web).
 *
 * Conventions identiques à catho-paroisses :
 *  - Fonction `t(key, params?)` via `useTranslation()`
 *  - Clés en dot-notation (`namespace.section.libelle`)
 *  - Interpolation `{{var}}` via params
 *  - 2 langues : fr (défaut) + en
 *  - Persistance localStorage (clé `economat_locale`)
 *
 * SÉCURITÉ : le retour de `t()` est destiné à un text node React
 * (`{t("key")}`) — ne JAMAIS passer à `dangerouslySetInnerHTML`.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

import frCommon from "../../i18n/locales/fr/common.json";
import enCommon from "../../i18n/locales/en/common.json";
import frLogin from "../../i18n/locales/fr/login.json";
import enLogin from "../../i18n/locales/en/login.json";
import frDashboard from "../../i18n/locales/fr/dashboard.json";
import enDashboard from "../../i18n/locales/en/dashboard.json";
import frParoisses from "../../i18n/locales/fr/paroisses.json";
import enParoisses from "../../i18n/locales/en/paroisses.json";
import frClerge from "../../i18n/locales/fr/clerge.json";
import enClerge from "../../i18n/locales/en/clerge.json";
import frComptabilite from "../../i18n/locales/fr/comptabilite.json";
import enComptabilite from "../../i18n/locales/en/comptabilite.json";
import frNotfound from "../../i18n/locales/fr/notfound.json";
import enNotfound from "../../i18n/locales/en/notfound.json";
import frSedec from "../../i18n/locales/fr/sedec.json";
import enSedec from "../../i18n/locales/en/sedec.json";
import frComptable from "../../i18n/locales/fr/comptable.json";
import enComptable from "../../i18n/locales/en/comptable.json";
import frPastoral from "../../i18n/locales/fr/pastoral.json";
import enPastoral from "../../i18n/locales/en/pastoral.json";
import frForms from "../../i18n/locales/fr/forms.json";
import enForms from "../../i18n/locales/en/forms.json";
import frApp from "../../i18n/locales/fr/app.json";
import enApp from "../../i18n/locales/en/app.json";

export type Locale = "fr" | "en";

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isReady: boolean;
}

const LOCALE_STORAGE_KEY = "economat_locale";
const DEFAULT_LOCALE: Locale = "fr";
const SUPPORTED_LOCALES: Locale[] = ["fr", "en"];

export const LOCALE_NAMES: Record<Locale, string> = {
  fr: "Français",
  en: "English",
};

type TranslationDict = Record<string, unknown>;

function mergeNamespaces(modules: Array<[string, unknown]>): TranslationDict {
  const out: TranslationDict = {};
  for (const [ns, dict] of modules) out[ns] = dict;
  return out;
}

const TRANSLATIONS: Record<Locale, TranslationDict> = {
  fr: mergeNamespaces([
    ["common", frCommon],
    ["login", frLogin],
    ["dashboard", frDashboard],
    ["paroisses", frParoisses],
    ["clerge", frClerge],
    ["comptabilite", frComptabilite],
    ["notfound", frNotfound],
    ["sedec", frSedec],
    ["comptable", frComptable],
    ["pastoral", frPastoral],
    ["forms", frForms],
    ["app", frApp],
  ]),
  en: mergeNamespaces([
    ["common", enCommon],
    ["login", enLogin],
    ["dashboard", enDashboard],
    ["paroisses", enParoisses],
    ["clerge", enClerge],
    ["comptabilite", enComptabilite],
    ["notfound", enNotfound],
    ["sedec", enSedec],
    ["comptable", enComptable],
    ["pastoral", enPastoral],
    ["forms", enForms],
    ["app", enApp],
  ]),
};

function getNestedValue(obj: TranslationDict, path: string): string | undefined {
  const keys = path.split(".");
  let cur: unknown = obj;
  for (const k of keys) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }
  return typeof cur === "string" ? cur : undefined;
}

function formatString(
  template: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    params[k] !== undefined ? String(params[k]) : `{{${k}}}`,
  );
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: ReactNode;
  forcedLocale?: Locale;
}

export function I18nProvider({ children, forcedLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (forcedLocale) return forcedLocale;
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
        return stored as Locale;
      }
    } catch {
      /* SSR or storage disabled */
    }
    return DEFAULT_LOCALE;
  });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    if (!SUPPORTED_LOCALES.includes(newLocale)) return;
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch {
      /* ignore */
    }
    setLocaleState(newLocale);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const dict = TRANSLATIONS[locale];
      const value = getNestedValue(dict, key);
      if (value === undefined) return key;
      return formatString(value, params);
    },
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, isReady }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return ctx;
}

export function useTranslation(): (
  key: string,
  params?: Record<string, string | number>,
) => string {
  return useI18n().t;
}
